import * as React from 'react';
import { Controller, FormProvider, useFormContext } from 'react-hook-form';

import { Label, Text } from '@/components/ui';

// Closed form scaffolding. Three exports:
//
// <Form form? onSubmit? grow?>{children}</Form>
//   Always renders a native <form> with `tw-gap-6` vertical spacing
//   between direct children. When `form` is provided (an RHF
//   useForm() return), Form also wraps the tree in <FormProvider>
//   so <FormField> siblings can subscribe to the context. Plain
//   form scenarios (component-level useState, no validation) omit
//   `form` and use <Form> purely as the layout primitive — keeping
//   the gap and `<form>` semantic without paying for RHF.
//
// <FormField name label required? info? disabled? grow? render />
//   Subscribes to RHF for `name`, renders a Label-row + the
//   caller-supplied control + (when present) an inline error and
//   info text. The control's RHF wiring is delivered through the
//   `render` prop because WM controls have inconsistent value/change
//   prop shapes (Input/Select use `value`/`onChange`, Checkbox/Switch
//   use `checked`/`onCheckedChange`, etc); rather than encode that
//   mapping inside FormField, callers adapt explicitly.
//
//   Validation lives at the form level — the caller wires a resolver
//   (e.g. zodResolver(schema)) into useForm() and FormField surfaces
//   whatever error RHF produces for `name`. There is no per-field
//   `rules` prop on purpose: the schema is one declarative artefact
//   defined once at the top of the component. For reactive inline
//   errors, set useForm({ mode: 'onChange' | 'onBlur' | 'onTouched' }).
//
//   Must be used inside a <Form form={form}> (FormProvider context
//   required).
//
// useDirtyValues(form)
//   Hook that returns a function for building "PATCH only what
//   changed" payloads on Edit screens. Lives next to Form here
//   (`./useDirtyValues`) so the source, its docstring, and the
//   primitives it pairs with all sit in the same folder. See that
//   file for the full rationale and usage example.
//
// Internal layout of FormField:
//   ┌──────────────────────────────────────┐
//   │ Label *                  Error msg   │   ← flex justify-between
//   │ Info text (optional, muted caption)  │
//   │ [        control input        ]      │
//   └──────────────────────────────────────┘
//
// The label/error pair sits on the same row with `flex
// justify-between`. The previous implementation overlapped them with
// absolute positioning and magic widths (pr-48 / w-44) — replaced
// with flex so there are no magic numbers and the layout is robust
// to long labels or messages (each side can truncate via `min-w-0`
// at the call site if needed).

export { useDirtyValues } from './useDirtyValues';

export const Form = ({ children, form, grow = false, onSubmit }) => {
    // `grow` opts the form into flex-fill behaviour: when its parent is
    // a flex column with available height, the form claims it so a
    // child field with `flex-1` (e.g. a FormField with `grow=true`
    // hosting a TransferList) can spread vertically. Default behaviour
    // — content-sized form — is preserved for every existing caller.
    const className = grow
        ? 'tw-flex tw-w-full tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-6'
        : 'tw-flex tw-w-full tw-flex-col tw-gap-6';

    const formElement = (
        <form className={className} onSubmit={onSubmit}>
            {children}
        </form>
    );

    return form ? <FormProvider {...form}>{formElement}</FormProvider> : formElement;
};

export const FormField = ({
    disabled = false,
    grow = false,
    info,
    label,
    name,
    render,
    required = false
}) => {
    const generatedId = React.useId();
    const id = `field-${generatedId}`;
    const { control } = useFormContext();
    // Label is optional: controls that carry their own label inline
    // (Checkbox/Switch with `label` prop) pass `label` undefined and
    // skip the label row entirely. Error still surfaces below.
    const hasLabel = Boolean(label);

    // `grow` opts the row into flex-fill behaviour: the FormField root
    // becomes `flex-1 min-h-0` so any height assigned by an ancestor
    // flex chain flows through it, and the render wrapper claims that
    // height for the control to consume. Used by controls that want to
    // expand vertically (e.g. the TransferList inside an aggregation
    // builder). Off by default — every other field stays content-sized
    // exactly like before.
    const rootClassName = grow
        ? 'tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-1'
        : 'tw-flex tw-flex-col tw-gap-1';

    // Whole row rendered inside Controller so the error message is
    // driven by the reactive `fieldState` it exposes — reading
    // `formState.errors` from useFormContext outside Controller does
    // not establish a render subscription, so manual setError calls
    // never re-render the row.
    return (
        <Controller
            control={control}
            name={name}
            render={({ field, fieldState }) => (
                <div className={rootClassName}>
                    {hasLabel && (
                        <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-2">
                            <Label htmlFor={id} required={required} disabled={disabled}>{label}</Label>
                            {fieldState.error?.message && (
                                <Text tone="destructive" variant="control">{String(fieldState.error.message)}</Text>
                            )}
                        </div>
                    )}
                    {info && (
                        <Text tone="muted" variant="caption">{info}</Text>
                    )}
                    {grow ? (
                        <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
                            {render({ field: { ...field, id }, fieldState })}
                        </div>
                    ) : (
                        render({ field: { ...field, id }, fieldState })
                    )}
                    {!hasLabel && fieldState.error?.message && (
                        <Text tone="destructive" variant="control">{String(fieldState.error.message)}</Text>
                    )}
                </div>
            )}
        />
    );
};
